import clsx from 'clsx';
import React, { useRef, useState, useEffect, type JSX } from 'react';
import { LinkComponentProps } from '../..';

export interface Tab {
  key: string;
  href: string;
  displayName: string | React.ReactNode;
}

export interface TabNavProps {
  tabs: Tab[];
  active: string;
  ariaLabel: string;
  LinkComponent: React.ComponentType<LinkComponentProps<Tab>>;
  fillWidth?: boolean;
  animated?: boolean;
}

// In tailwind sizing (1 = 0.25rem)
const SPACING = 2;

export const TabNav = ({
  LinkComponent,
  tabs,
  ariaLabel,
  active,
  fillWidth = false,
  animated = false,
}: TabNavProps): JSX.Element => {
  const activeIndex = tabs.findIndex(tab => tab.href === active);
  const [childWidths, setChildWidths] = useState([]);
  const wrapperRef = useRef<undefined | HTMLUListElement>(undefined);

  useEffect(() => {
    const calculateWidths = () => {
      setChildWidths(
        [...(wrapperRef?.current?.children || [])].reduce((acc, child) => {
          // @ts-ignore
          child.getBoundingClientRect().width > 0 && acc.push(child.getBoundingClientRect().width);
          return acc;
        }, []),
      );
    };

    if (wrapperRef.current) {
      calculateWidths();
    }

    window.addEventListener('resize', calculateWidths);

    // Remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', calculateWidths);
    };
  }, [tabs]);

  return (
    <nav aria-label={ariaLabel} className="relative">
      <ul
        className={clsx(`list-none flex gap-x-2`, fillWidth && `border-action-outline-secondary-enabled border-b-2`)}
        //@ts-ignore
        ref={wrapperRef}
      >
        {tabs.map(item => (
          <li className="text-body-lg" key={item.key}>
            <LinkComponent item={item}>
              <a
                aria-current={item.href === active ? 'page' : 'false'}
                className={clsx(
                  `
                    block px-2 py-1 text-action-content-secondary-enabled rounded-md
                    hover:text-action-content-secondary-hover hover:bg-action-fill-secondary-hover
                  `,
                  active === item.href && `text-action-content-link-enabled!`,
                )}
              >
                {item.displayName}
              </a>
            </LinkComponent>
          </li>
        ))}
      </ul>
      <div
        className={clsx(
          `absolute bottom-0 h-0.5 bg-action-content-link-enabled z-10`,
          animated && `transition-all motion-reduce:transition-none`,
        )}
        style={{
          width: `${childWidths[activeIndex]}px`,
          left: childWidths.slice(0, activeIndex).reduce((total, width) => (total += width), 0),
          marginLeft: `${activeIndex * (SPACING / 4)}rem`,
        }}
      />
    </nav>
  );
};
